import { useCallback, useEffect, useState } from 'react';
import { exportThemeConfig, getAllThemeNames } from '../../../sources/theme';
import { getAllEnvironments } from '../../../sources/environments';
import { useTenant } from '../../TenantProvider';
import type { Environment } from '../../../types/environment';

const useThemeExport = (onExportSuccess?: () => void) => {
    const [selectedEnvironment, setSelectedEnvironment] = useState('');
    const [selectedTheme, setSelectedTheme] = useState('');
    const [themes, setThemes] = useState<string[]>([]);
    const [environments, setEnvironments] = useState<Environment[]>([]);
    const tenant = useTenant();

    useEffect(() => {
        (async () => {
            if (tenant.tenantSettings?.environments) {
                const environments = await getAllEnvironments();
                setEnvironments(environments);
            }
        })();
    }, [tenant.tenantSettings?.environments]);

    useEffect(() => {
        const loadThemes = async () => {
            const themes = await getAllThemeNames(selectedEnvironment);
            setThemes(themes);
            // Clear selected theme when environment changes since it may not exist in the new environment
            setSelectedTheme('');
        };

        loadThemes();
    }, [selectedEnvironment]);

    const onEnvironmentSelect = (selectedOption: React.ChangeEvent<HTMLSelectElement>) =>
        setSelectedEnvironment(selectedOption?.target?.value ?? '');

    const onExport = async () => {
        await exportThemeConfig(selectedTheme);
        onExportSuccess?.();
    };

    const resetSelection = useCallback(() => {
        setSelectedTheme('');
        setSelectedEnvironment('');
    }, []);

    const isThemeSelected =
        (tenant.tenantSettings?.environments &&
            selectedEnvironment !== '' &&
            selectedTheme !== '') ||
        (!tenant.tenantSettings?.environments && selectedTheme !== '');

    return {
        selectedEnvironment,
        selectedTheme,
        setSelectedTheme,
        themes,
        environments,
        onEnvironmentSelect,
        onExport,
        tenant,
        isThemeSelected,
        resetSelection,
    };
};

export default useThemeExport;
